import React from "react";
import { useParams, Link } from "react-router-dom";
import { trips, site } from "../data/mock";
import { Reveal, Eyebrow, CTASection } from "../components/common";
import { Button } from "../components/ui/button";
import { useDeposit } from "../context/DepositContext";
import { Check, Clock, Users, DollarSign, Anchor, Phone, ArrowLeft, MessageCircle, Fish, Waves, MapPin, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal as _R } from "../components/common";
import { useTranslation } from "react-i18next";
import { useTrip } from "../i18n/content";

const compareData = {
  inshore: {
    label: "Full Day Inshore",
    icon: MapPin,
    tag: "Close to shore",
    accent: "#0e9488",
    points: [
      "Fish the beaches, rocky points & reef structure",
      "Sight-cast to trophy roosterfish & cubera snapper",
      "Non-stop bites — jacks, mackerel, needlefish & more",
      "Short runs, calmer water — great for families & kids",
      "Light-tackle action all day long",
      "Best May–October for peak roosterfish runs",
    ],
    to: "/trips/full-day-inshore",
  },
  offshore: {
    label: "Full Day Offshore",
    icon: Waves,
    tag: "Out to the blue",
    accent: "#1c6ba3",
    points: [
      "Run offshore for the biggest game fish",
      "Target marlin, sailfish & giant yellowfin tuna",
      "Trolling the deep blue water & seamounts",
      "Longer runs — built for dedicated anglers",
      "Combines inshore start with offshore push",
      "Marlin & tuna peak May–September, sails Dec–April",
    ],
    to: "/trips/full-day-offshore",
  },
};

const CompareBlock = ({ current }) => {
  const { t } = useTranslation();
  const order = current === "full-day-inshore" ? ["inshore", "offshore"] : ["offshore", "inshore"];
  return (
    <section className="py-16 bg-[#081826] border-y border-white/10">
      <div className="container-x">
        <_R>
          <span className="inline-flex items-center gap-2 text-[#f5a524] font-heading tracking-[0.25em] uppercase text-xs mb-3">
            <span className="h-px w-8 bg-[#f5a524]" /> {t("tripDetail.whichTripEyebrow")}
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">{t("tripDetail.compareTitle")}</h2>
          <p className="mt-4 text-slate-300 text-base md:text-lg max-w-2xl">{t("tripDetail.compareSub")}</p>
        </_R>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {order.map((key, i) => {
            const c = compareData[key];
            const Icon = c.icon;
            const isCurrent = c.to === `/trips/${current}`;
            return (
              <_R key={key} delay={i * 0.1}>
                <div className={`h-full rounded-2xl border p-7 flex flex-col ${isCurrent ? "border-[#f5a524] bg-[#0e2740]" : "border-white/10 bg-[#0e2740]/60"}`} data-testid={`compare-card-${key}`}>
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-11 w-11 rounded-xl" style={{ backgroundColor: `${c.accent}22`, color: c.accent }}><Icon className="h-6 w-6" /></span>
                    <div>
                      <p className="text-xs uppercase tracking-widest" style={{ color: c.accent }}>{c.tag}</p>
                      <h3 className="font-heading text-2xl text-white">{c.label}</h3>
                    </div>
                    {isCurrent && <span className="ml-auto rounded-full bg-[#f5a524] px-3 py-1 text-xs font-bold text-[#0a1f33]">{t("tripDetail.youreHere")}</span>}
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {c.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-slate-200"><Fish className="h-4 w-4 shrink-0 mt-0.5" style={{ color: c.accent }} /> {p}</li>
                    ))}
                  </ul>
                  {!isCurrent && (
                    <Link to={c.to} className="mt-6">
                      <Button variant="outline" className="w-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white gap-2" data-testid={`compare-view-${key}`}>View {c.label} <ArrowRight className="h-4 w-4" /></Button>
                    </Link>
                  )}
                </div>
              </_R>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const TripDetail = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { openDeposit } = useDeposit();
  const trip = useTrip(slug);
  if (!trip) return (
    <div className="container-x py-40 text-center"><h1 className="font-display text-4xl text-white">Trip not found</h1><Link to="/trips" className="text-[#f5a524] mt-4 inline-block">Back to trips</Link></div>
  );
  return (
    <>
      <Seo
        title={`${trip.name} — San Juan del Sur Fishing Charter | Hog Tide`}
        description={`${trip.short} Private ${trip.duration} charter in San Juan del Sur, Nicaragua from $${trip.price}. Book with Hog Tide Fishing.`}
        path={`/trips/${trip.slug}`}
        image={trip.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: `${trip.name} — Hog Tide Fishing Charter`,
          description: trip.short,
          image: trip.image,
          brand: { "@type": "Brand", name: "Hog Tide Fishing" },
          offers: {
            "@type": "Offer",
            price: String(trip.price),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: `https://hogtidefishing.com/trips/${trip.slug}`,
          },
        }}
      />
      <section className="relative min-h-[56vh] flex items-end overflow-hidden">
        <img src={trip.banner} alt={trip.name} className="absolute inset-0 h-full w-full object-cover animate-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f33] via-[#0a1f33]/60 to-transparent" />
        <div className="container-x relative z-10 pb-12 pt-28">
          <Link to="/trips" className="inline-flex items-center gap-1 text-slate-300 hover:text-[#f5a524] text-sm mb-3"><ArrowLeft className="h-4 w-4" /> All trips</Link>
          <Eyebrow>{trip.hook}</Eyebrow>
          <h1 className="font-display text-5xl md:text-6xl text-white text-shadow-hero">{trip.name}</h1>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-lg text-slate-200 leading-relaxed">{trip.description}</p>
              <h3 className="font-heading text-2xl text-white mt-8 mb-4">What's included</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {trip.features.map((f) => (
                  <div key={f} className="flex items-start gap-3"><Check className="h-5 w-5 text-[#f5a524] shrink-0 mt-0.5" /><span className="text-slate-200 text-sm">{f}</span></div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl border border-white/10 bg-[#0e2740] p-6">
              <div className="flex items-end gap-2">
                <span className="font-display text-5xl text-[#f5a524]">${trip.price}</span>
                <span className="text-slate-400 text-sm mb-2">{trip.priceFull ? `– $${trip.priceFull}` : "per trip"}</span>
              </div>
              <div className="space-y-3 mt-5 text-sm">
                <p className="flex items-center gap-2 text-slate-200"><Clock className="h-4 w-4 text-[#f5a524]" /> {trip.duration}</p>
                <p className="flex items-center gap-2 text-slate-200"><Users className="h-4 w-4 text-[#f5a524]" /> {trip.capacity}</p>
                <p className="flex items-center gap-2 text-slate-200"><DollarSign className="h-4 w-4 text-[#f5a524]" /> Reserve by WhatsApp or phone</p>
              </div>
              <Button className="btn-gold w-full mt-6 h-12 gap-2" onClick={() => openDeposit(trip)}><MessageCircle className="h-4 w-4" /> Book on WhatsApp</Button>
              <a href={`tel:${site.phoneRaw}`}><Button variant="outline" className="w-full mt-3 border-white/20 text-white bg-transparent hover:bg-white/10 hover:text-white gap-2"><Phone className="h-4 w-4" /> {site.phone}</Button></a>
              <p className="mt-4 flex items-start gap-2 text-xs text-slate-300" data-testid="trip-whatsapp-note">
                <span className="relative mt-1 flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#25D366]" />
                </span>
                {site.whatsappNote}
              </p>
            </div>
          </div>
        </div>
      </section>
      {(trip.slug === "full-day-inshore" || trip.slug === "full-day-offshore") && <CompareBlock current={trip.slug} />}
      <CTASection image={trip.banner} />
    </>
  );
};

export default TripDetail;
